<template>
  <div class="dossier-page">
    <!-- Page chrome — breadcrumb + action buttons. Lives inside the
         admin shell, not as a global top nav, so the app sidebar stays
         visible to the left. -->
    <header class="ud-nav">
      <span class="brand">PAYTON<em>.KZ</em></span>
      <span class="bc-sep">/</span>
      <router-link :to="{ name: 'Users' }" class="bc-link">KYC-панель</router-link>
      <span class="bc-sep">/</span>
      <span class="bc-cur">{{ fioFull || 'Досье клиента' }}</span>

      <div class="nav-r">
        <button type="button" class="nav-btn" @click="onPrint">
          <span class="i">🖨</span> Распечатать
        </button>
        <button type="button" class="nav-btn" @click="onExportPdf">
          <span class="i">⬇</span> Экспорт PDF
        </button>
        <button
          type="button"
          class="nav-btn warn"
          :disabled="freezeBusy"
          @click="openFreezeModal"
        >
          <span class="i">❄</span>
          {{ isAccountFrozen ? 'Разморозить аккаунт' : 'Заморозить аккаунт' }}
        </button>
        <button
          type="button"
          class="nav-btn danger"
          :disabled="blockBusy"
          @click="toggleBlock"
        >
          <span class="i">⊘</span>
          {{ isBlocked ? 'Разблокировать' : 'Заблокировать' }}
        </button>
      </div>
    </header>

    <!-- Заморозка / разморозка аккаунта: обязательный комментарий для
         внутреннего аудита. Заморозка не запрещает вход в ЛК, но закрывает
         сервисы; клиенту уходит уведомление. -->
    <b-modal
      id="freeze-dossier-modal"
      :title="isAccountFrozen ? 'Разморозить аккаунт' : 'Заморозить аккаунт'"
      :ok-title="isAccountFrozen ? 'Разморозить' : 'Заморозить'"
      :ok-variant="isAccountFrozen ? 'success' : 'warning'"
      cancel-title="Отмена"
      cancel-variant="outline-secondary"
      :ok-disabled="freezeBusy || !freezeReason.trim()"
      @ok.prevent="submitFreeze"
      centered
    >
      <p v-if="!isAccountFrozen" class="mb-2">
        Клиент сохранит доступ к личному кабинету, но будет полностью
        ограничен в сервисах (пополнение и др.). Ему уйдёт уведомление о
        заморозке. Действие обратимо.
      </p>
      <p v-else class="mb-2">
        Клиенту снова станут доступны сервисы. Ему уйдёт уведомление о
        снятии заморозки.
      </p>
      <b-form-group label="Комментарий для внутреннего аудита (обязательно)">
        <b-form-textarea
          v-model="freezeReason"
          rows="3"
          placeholder="Например: подозрительный источник средств по результатам KYT, тикет #123"
        />
      </b-form-group>
    </b-modal>

    <div class="wrap">
      <!-- PAGE HEAD: avatar + name + IIN + status / risk / AML meta -->
      <div class="ph">
        <div class="ph-left">
          <div class="ph-ava">{{ initials || '—' }}</div>
          <div>
            <div class="ph-name">{{ fioFull || '—' }}</div>
            <div class="ph-meta">
              <span>ИИН: <b>{{ user.iin || '—' }}</b></span>
              <span>·</span>
              <span class="badge" :class="accountStatusClass">
                <span class="bd"></span>{{ accountStatusLabel }}
              </span>
              <span>·</span>
              <span :class="riskColorClass">{{ riskLabel }} риск</span>
              <span>·</span>
              <template v-if="amlFlags.length">
                <span
                  v-for="flag in amlFlags"
                  :key="flag.code"
                  class="aml-flag"
                  :class="flag.cssClass"
                >{{ flag.code }}</span>
              </template>
              <span v-else class="muted-mini">Флагов нет</span>
            </div>
          </div>
        </div>
      </div>

      <!-- STATUS BAR — 5 KPI tiles mirroring the mock header strip. -->
      <div class="status-bar">
        <div class="sb-item">
          <div class="sb-lbl">Тип клиента</div>
          <div class="sb-val">{{ user.isOrg ? 'Юр. лицо' : 'Individual' }}</div>
        </div>
        <div class="sb-item">
          <div class="sb-lbl">Классификация</div>
          <div class="sb-val">{{ classificationLabel }}</div>
        </div>
        <div class="sb-item">
          <div class="sb-lbl">Статус аккаунта</div>
          <div class="sb-val">
            <span class="badge" :class="accountStatusClass">
              <span class="bd"></span>{{ accountStatusLabel }}
            </span>
          </div>
        </div>
        <div class="sb-item">
          <div class="sb-lbl">Лимит / мес</div>
          <div class="sb-val">{{ limitLabel }}</div>
        </div>
        <div class="sb-item">
          <div class="sb-lbl">Риск / Скоринг</div>
          <div class="sb-val">{{ riskLabel }} · {{ amlScore }}</div>
        </div>
      </div>

      <!-- АНКЕТЫ — for now: single row reflecting the current snapshot.
           Multi-anketa history lands once the backend endpoint exists
           (see pending task in repo) — UI just adds rows then. -->
      <div class="anket-panel">
        <div class="anket-panel-head">
          <div class="anket-head-left">
            <span class="dot-ic">📋</span>
            <span class="anket-panel-title">Список анкет клиента</span>
            <span class="anket-cnt">{{ anketas.length }} {{ anketWord(anketas.length) }}</span>
          </div>
          <span class="muted-mini">Активной может быть только одна анкета</span>
        </div>
        <table class="at">
          <thead>
            <tr>
              <th style="width:40px">№</th>
              <th>Дата создания</th>
              <th>Дата закрытия</th>
              <th>Статус аккаунта</th>
              <th>Уровень риска</th>
              <th>Классификация</th>
              <th>Лимит / мес</th>
              <th>Статус анкеты</th>
              <th style="width:32px"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="anket in anketas"
              :key="anket.num"
              :class="{ sel: selectedAnketNum === anket.num }"
              @click="selectAnket(anket.num)"
            >
              <td>
                <span class="a-num" :class="anket.status === 'active' ? 'act' : 'arch'">{{ anket.num }}</span>
              </td>
              <td>{{ anket.created || '—' }}</td>
              <td>{{ anket.closed || '—' }}</td>
              <td>
                <span class="badge" :class="anket.accountStatusCss">
                  <span class="bd"></span>{{ anket.accountStatusLabel }}
                </span>
              </td>
              <td><span :class="anket.riskCss">{{ anket.risk }}</span></td>
              <td style="font-size:11px">{{ anket.cls }}</td>
              <td style="font-weight:700">{{ anket.limit }}</td>
              <td>
                <span class="a-badge" :class="anket.status === 'active' ? 'active' : 'arch'">
                  {{ anket.status === 'active' ? 'Актуальная' : 'Архивная' }}
                </span>
              </td>
              <td class="row-arrow">›</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- DOSSIER CONTENT — driven by the selected anket. -->
      <template v-if="selectedAnket">
        <div class="dos-label" :class="selectedAnket.status === 'active' ? 'active' : 'archive'">
          <span class="i">{{ selectedAnket.status === 'active' ? '✓' : '🗄' }}</span>
          <template v-if="selectedAnket.status === 'active'">
            <b>Активная анкета №&nbsp;{{ selectedAnket.num }}</b> · Создана {{ selectedAnket.created }}
          </template>
          <template v-else>
            <b>Архивная анкета №&nbsp;{{ selectedAnket.num }}</b> · {{ selectedAnket.created }} — {{ selectedAnket.closed }}
          </template>
        </div>

        <!-- MEDIA: photo + video from Sumsub verification -->
        <div v-if="hasMedia" class="media-grid">
          <div class="media-card">
            <div class="media-card-head">
              <div class="media-card-title">Фото клиента</div>
              <span class="media-card-status" :class="user.hasPhoto ? 'mcs-ok' : 'mcs-no'">
                {{ user.hasPhoto ? 'Загружено' : 'Отсутствует' }}
              </span>
            </div>
            <div class="media-card-body">
              <div v-if="!user.hasPhoto" class="photo-placeholder">
                <div class="photo-placeholder-icon">📷</div>
                <div class="photo-placeholder-text">Фото не загружено</div>
                <div class="photo-placeholder-sub">Получается из Sumsub</div>
              </div>
              <div v-else class="photo-avatar-large">{{ initials }}</div>
            </div>
          </div>
          <div class="media-card">
            <div class="media-card-head">
              <div class="media-card-title">Видео-верификация</div>
              <span class="media-card-status" :class="user.hasVideo ? 'mcs-ok' : 'mcs-no'">
                {{ user.hasVideo ? 'Загружено' : 'Отсутствует' }}
              </span>
            </div>
            <div class="media-card-body">
              <div class="video-placeholder">
                <div class="video-placeholder-icon">🎥</div>
                <div class="photo-placeholder-text">
                  {{ user.hasVideo ? 'Нажмите для просмотра' : 'Видео не загружено' }}
                </div>
                <div class="photo-placeholder-sub">Sumsub liveness check</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 1. ЛИЧНЫЕ ДАННЫЕ -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('personal')">
            <div class="sec-title"><span class="i">👤</span> Личные данные</div>
            <span class="sec-arrow" :class="{ open: openSections.personal }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.personal }">
            <div class="subsec">Кириллица</div>
            <div class="fg">
              <div class="f"><label>Фамилия</label><div :class="cellClass(user.lastName)">{{ fallback(user.lastName) }}</div></div>
              <div class="f"><label>Имя</label><div :class="cellClass(user.firstName)">{{ fallback(user.firstName) }}</div></div>
              <div class="f"><label>Отчество</label><div :class="cellClass(user.thirdName)">{{ fallback(user.thirdName) }}</div></div>
              <div class="f"><label>Дата рождения</label><div :class="cellClass(user.dob)">{{ user.dob ? formatDate(user.dob) : fallback(user.dob) }}</div></div>
              <div class="f"><label>Место рождения</label><div :class="cellClass(user.birthPlace)">{{ fallback(user.birthPlace) }}</div></div>
              <div class="f"><label>ИИН</label><div :class="cellClass(user.iin)">{{ fallback(user.iin) }}</div></div>
            </div>

            <div class="subsec">Гражданство и контакты</div>
            <div class="fg">
              <div class="f"><label>Гражданство</label><div :class="cellClass(countryLabel(user.nationality))">{{ fallback(countryLabel(user.nationality)) }}</div></div>
              <div class="f"><label>Резидентство</label><div :class="cellClass(countryLabel(user.residenceCountry))">{{ fallback(countryLabel(user.residenceCountry)) }}</div></div>
              <div class="f"><label>Тип клиента</label><div class="v">{{ user.isOrg ? 'Юр. лицо' : 'Individual' }}</div></div>
              <div class="f"><label>Email</label><div :class="cellClass(user.email)">{{ fallback(user.email) }}</div></div>
              <div class="f"><label>Телефон</label><div :class="cellClass(user.phoneNumber)">{{ fallback(user.phoneNumber) }}</div></div>
            </div>

            <div class="subsec">ПДЛ / ИПДЛ</div>
            <div class="fg">
              <div class="f">
                <label>Причастность к ИПДЛ</label>
                <div class="v" :style="{ color: pepInvolved ? '#DC2626' : '#059669', fontWeight: 700 }">
                  {{ pepInvolved ? 'Да' : 'Нет' }}
                </div>
              </div>
              <div class="f"><label>Должность ПДЛ</label><div :class="cellClass(user.pepPosition)">{{ fallback(user.pepPosition) }}</div></div>
              <div class="f"><label>ФИО ПДЛ</label><div :class="cellClass(user.pepFio)">{{ fallback(user.pepFio) }}</div></div>
            </div>
          </div>
        </section>

        <!-- 2. ДОКУМЕНТ -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('document')">
            <div class="sec-title"><span class="i">🪪</span> Удостоверяющий документ</div>
            <span class="sec-arrow" :class="{ open: openSections.document }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.document }">
            <div class="fg">
              <div class="f"><label>Тип документа</label><div :class="cellClass(docTypeLabel)">{{ fallback(docTypeLabel) }}</div></div>
              <div class="f"><label>Серия</label><div :class="cellClass(doc.documentSeries)">{{ fallback(doc.documentSeries) }}</div></div>
              <div class="f"><label>Номер</label><div :class="cellClass(doc.documentNumber)">{{ fallback(doc.documentNumber) }}</div></div>
              <div class="f"><label>Кем выдан</label><div :class="cellClass(doc.issuedBy)">{{ fallback(doc.issuedBy) }}</div></div>
              <div class="f"><label>Дата выдачи</label><div :class="cellClass(doc.issueDate)">{{ fallback(doc.issueDate) }}</div></div>
              <div class="f"><label>Срок действия</label><div :class="cellClass(doc.expiryDate)">{{ fallback(doc.expiryDate) }}</div></div>
            </div>
          </div>
        </section>

        <!-- 3. АДРЕСА -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('address')">
            <div class="sec-title"><span class="i">📍</span> Адреса</div>
            <span class="sec-arrow" :class="{ open: openSections.address }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.address }">
            <div class="subsec">Адрес регистрации</div>
            <div class="fg">
              <div class="f"><label>Страна</label><div :class="cellClass(countryLabel(user.regCountry))">{{ fallback(countryLabel(user.regCountry)) }}</div></div>
              <div class="f"><label>Индекс</label><div :class="cellClass(user.regIndex)">{{ fallback(user.regIndex) }}</div></div>
              <div class="f"><label>Регион</label><div :class="cellClass(user.regRegion)">{{ fallback(user.regRegion) }}</div></div>
              <div class="f"><label>Город</label><div :class="cellClass(user.regCity)">{{ fallback(user.regCity) }}</div></div>
              <div class="f full"><label>Адрес</label><div :class="cellClass(user.regAddr)">{{ fallback(user.regAddr) }}</div></div>
            </div>
            <div class="subsec">Адрес фактического проживания</div>
            <div class="fg">
              <div class="f"><label>Страна</label><div :class="cellClass(countryLabel(user.realCountry))">{{ fallback(countryLabel(user.realCountry)) }}</div></div>
              <div class="f"><label>Индекс</label><div :class="cellClass(user.realIndex)">{{ fallback(user.realIndex) }}</div></div>
              <div class="f"><label>Регион</label><div :class="cellClass(user.realRegion)">{{ fallback(user.realRegion) }}</div></div>
              <div class="f"><label>Город</label><div :class="cellClass(user.realCity)">{{ fallback(user.realCity) }}</div></div>
              <div class="f full"><label>Адрес</label><div :class="cellClass(user.realAddr)">{{ fallback(user.realAddr) }}</div></div>
            </div>
          </div>
        </section>

        <!-- 4. ФИНАНСЫ -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('finance')">
            <div class="sec-title"><span class="i">$</span> Финансы и занятость</div>
            <span class="sec-arrow" :class="{ open: openSections.finance }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.finance }">
            <div class="fg">
              <div class="f"><label>Место работы</label><div :class="cellClass(user.workplace)">{{ fallback(user.workplace) }}</div></div>
              <div class="f"><label>Должность</label><div :class="cellClass(user.jobTitle)">{{ fallback(user.jobTitle) }}</div></div>
              <div class="f"><label>Вид источника</label><div :class="cellClass(user.moneySource)">{{ fallback(user.moneySource) }}</div></div>
              <div class="f full"><label>Характеристика источника</label><div :class="cellClass(user.wealthSource)">{{ fallback(user.wealthSource) }}</div></div>
            </div>
          </div>
        </section>

        <!-- 5. КЛАССИФИКАЦИЯ И ДОГОВОР -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('classification')">
            <div class="sec-title"><span class="i">🎓</span> Классификация и договор</div>
            <span class="sec-arrow" :class="{ open: openSections.classification }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.classification }">
            <div class="fg">
              <div class="f"><label>Классификация</label><div class="v">{{ classificationLabel }}</div></div>
              <div class="f"><label>Уровень риска</label><div class="v" :class="riskColorClass">{{ riskLabel }}<span v-if="riskIsOverridden" class="risk-manual-tag">вручную</span></div></div>
              <div class="f"><label>Скоринг риска</label><div class="v">{{ amlScore }}</div></div>
              <div class="f"><label>Лимит / мес</label><div class="v">{{ limitLabel }}</div></div>
            </div>

            <template v-if="reclassifications.length">
              <div class="subsec">История реклассификаций</div>
              <div v-for="(item, idx) in reclassifications" :key="idx" class="recls-item">
                <div class="recls-header">
                  <div class="recls-title">
                    {{ item.from }} → {{ item.to }}
                  </div>
                  <span class="recls-result" :style="{ color: item.resultColor }">{{ item.resultLabel }}</span>
                </div>
                <div class="recls-date">Подана: {{ item.submittedAt }}</div>
                <div class="recls-votes">
                  <div class="rv-box" :style="item.complianceBorder">
                    <div class="rv-role">Комплаенс-офицер</div>
                    <template v-if="item.compliance">
                      <div class="rv-result" :style="{ color: item.compliance.color }">{{ item.compliance.label }}</div>
                      <div class="rv-comment">{{ item.compliance.comment || '—' }}</div>
                      <div class="rv-when">{{ item.compliance.when }}</div>
                    </template>
                    <div v-else-if="!item.needsCompliance" class="rv-pending">Не требуется (путь через тест)</div>
                    <div v-else class="rv-pending">Ожидает решения</div>
                  </div>
                  <div class="rv-box" :style="item.mlroBorder">
                    <div class="rv-role">MLRO</div>
                    <template v-if="item.mlro">
                      <div class="rv-result" :style="{ color: item.mlro.color }">{{ item.mlro.label }}</div>
                      <div class="rv-comment">{{ item.mlro.comment || '—' }}</div>
                      <div class="rv-when">{{ item.mlro.when }}</div>
                    </template>
                    <div v-else class="rv-pending">Ожидает решения</div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </section>

        <!-- 6. AML -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('aml')">
            <div class="sec-title"><span class="i">🛡</span> AML-профиль</div>
            <span class="sec-arrow" :class="{ open: openSections.aml }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.aml }">
            <div class="aml-score-wrap">
              <div class="aml-score-num" :class="amlScoreColorClass">{{ amlScore }}</div>
              <div>
                <div class="aml-score-caption">Скоринг риска — {{ riskLabel }}</div>
                <div class="aml-score-bar">
                  <div
                    class="aml-score-fill"
                    :style="{ width: amlScore + '%', background: amlScoreFillColor }"
                  ></div>
                </div>
              </div>
            </div>
            <div class="fg">
              <div class="f"><label>AML-флаги</label><div class="v">{{ amlFlagsLabel }}</div></div>
              <div class="f"><label>Статус алерта</label><div :class="cellClass(amlAlertStatus)">{{ fallback(amlAlertStatus) }}</div></div>
              <div class="f full"><label>Результаты мониторинга операций</label><div :class="cellClass(amlMonitoringNote)">{{ fallback(amlMonitoringNote) }}</div></div>
            </div>
          </div>
        </section>

        <!-- 7a. ДОКУМЕНТЫ АНКЕТЫ — identification + biometry.
             Sourced from /exchange/userdetails (user.documents). -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('docs')">
            <div class="sec-title"><span class="i">📁</span> Документы анкеты</div>
            <span class="sec-arrow" :class="{ open: openSections.docs }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.docs }">
            <template v-if="docList.length">
              <div
                class="doc-item"
                :class="{ 'doc-item-disabled': !docItem.docPath }"
                v-for="docItem in docList"
                :key="docItem.id"
                @click="downloadDocItem(docItem)"
              >
                <div class="doc-thumb">📄</div>
                <div class="doc-info">
                  <div class="doc-name">{{ docItem.title }}</div>
                  <div class="doc-meta">{{ docItem.meta }}</div>
                </div>
                <span class="ds" :class="docItem.statusCss">{{ docItem.statusLabel }}</span>
                <button
                  type="button"
                  class="doc-dl"
                  :title="docItem.docPath ? 'Скачать' : 'Файл недоступен'"
                  :disabled="!docItem.docPath"
                  @click.stop="downloadDocItem(docItem)"
                >⬇</button>
              </div>
            </template>
            <div v-else class="muted-mini">Документы анкеты пока не загружены</div>
          </div>
        </section>

        <!-- 7b. ОБЩИЕ ДОКУМЕНТЫ КЛИЕНТА — SoF и квалификационные
             документы. Берутся из всех заявок на классификацию,
             дедуплицируются по (docType, fileUrl) и группируются по
             зоне ответственности рецензента: MLRO (финансовые) и
             комплаенс-офицер (квалификационные). -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('generalDocs')">
            <div class="sec-title"><span class="i">📂</span> Общие документы клиента</div>
            <span class="sec-arrow" :class="{ open: openSections.generalDocs }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.generalDocs }">
            <template v-if="hasClientGeneralDocs">
              <div class="doc-groups">
                <div v-if="clientGeneralDocs.financial.length">
                  <div class="dg-title">Финансовые · MLRO</div>
                  <div
                    class="doc-item"
                    :class="{ 'doc-item-disabled': !docItem.docPath }"
                    v-for="docItem in clientGeneralDocs.financial"
                    :key="'fin-' + docItem.id"
                    @click="downloadDocItem(docItem)"
                  >
                    <div class="doc-thumb">📄</div>
                    <div class="doc-info">
                      <div class="doc-name">{{ docItem.title }}</div>
                      <div class="doc-meta">{{ docItem.meta }}</div>
                    </div>
                    <span class="ds" :class="docItem.statusCss">{{ docItem.statusLabel }}</span>
                    <button
                      type="button"
                      class="doc-dl"
                      :title="docItem.docPath ? 'Скачать' : 'Файл недоступен'"
                      :disabled="!docItem.docPath"
                      @click.stop="downloadDocItem(docItem)"
                    >⬇</button>
                  </div>
                </div>
                <div v-if="clientGeneralDocs.qualifying.length">
                  <div class="dg-title">Квалификационные · Комплаенс</div>
                  <div
                    class="doc-item"
                    :class="{ 'doc-item-disabled': !docItem.docPath }"
                    v-for="docItem in clientGeneralDocs.qualifying"
                    :key="'q-' + docItem.id"
                    @click="downloadDocItem(docItem)"
                  >
                    <div class="doc-thumb">🏆</div>
                    <div class="doc-info">
                      <div class="doc-name">{{ docItem.title }}</div>
                      <div class="doc-meta">{{ docItem.meta }}</div>
                    </div>
                    <span class="ds" :class="docItem.statusCss">{{ docItem.statusLabel }}</span>
                    <button
                      type="button"
                      class="doc-dl"
                      :title="docItem.docPath ? 'Скачать' : 'Файл недоступен'"
                      :disabled="!docItem.docPath"
                      @click.stop="downloadDocItem(docItem)"
                    >⬇</button>
                  </div>
                </div>
                <div v-if="clientGeneralDocs.history.length" class="dg-history">
                  <div class="dg-title">История документов</div>
                  <div
                    class="doc-item"
                    :class="{ 'doc-item-disabled': !docItem.docPath }"
                    v-for="docItem in clientGeneralDocs.history"
                    :key="'h-' + docItem.id"
                    @click="downloadDocItem(docItem)"
                  >
                    <div class="doc-thumb">📄</div>
                    <div class="doc-info">
                      <div class="doc-name">{{ docItem.title }}</div>
                      <div class="doc-meta">{{ docItem.group }} · {{ docItem.meta }}</div>
                    </div>
                    <span class="ds" :class="docItem.statusCss">{{ docItem.statusLabel }}</span>
                    <button
                      type="button"
                      class="doc-dl"
                      :title="docItem.docPath ? 'Скачать' : 'Файл недоступен'"
                      :disabled="!docItem.docPath"
                      @click.stop="downloadDocItem(docItem)"
                    >⬇</button>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="muted-mini">Общие документы пока не загружены</div>
          </div>
        </section>

        <!-- 8. БАЛАНСЫ И ДЕПОЗИТЫ -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('balances')">
            <div class="sec-title"><span class="i">💰</span> Балансы и депозиты</div>
            <span class="sec-arrow" :class="{ open: openSections.balances }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.balances }">

            <div class="subsec">Текущие балансы</div>
            <div v-if="balances.length" class="bal-grid">
              <div v-for="b in balances" :key="b.balanceId" class="bal-card">
                <div class="bal-asset">
                  {{ b.asset }}<span v-if="b.network" class="bal-net">{{ b.network }}</span>
                </div>
                <div class="bal-row">
                  <span class="bal-row-lbl">Доступно</span>
                  <span class="bal-row-val">{{ formatAmount(b.available, b.asset) }}</span>
                </div>
                <div class="bal-row">
                  <span class="bal-row-lbl">В резерве</span>
                  <span class="bal-row-val bal-locked">{{ formatAmount(b.locked, b.asset) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="muted-mini">Балансы пока не созданы</div>

            <div class="subsec">История депозитов</div>
            <div v-if="deposits.length">
              <div v-for="dep in deposits" :key="dep.depositId" class="dep-item">
                <div class="dep-head">
                  <div class="dep-title">
                    <span class="dep-id">#{{ dep.depositId }}</span>
                    <span class="dep-status" :class="depositStatusCss(dep.status)">{{ depositStatusLabel(dep.status) }}</span>
                    <span class="dep-source" v-if="dep.source === 'MANUAL_CREDIT'">Ручное начисление</span>
                  </div>
                  <div class="dep-date">{{ formatDateTime(dep.confirmedAt || dep.createdAt) }}</div>
                </div>
                <div class="dep-body">
                  <div class="dep-amount">
                    <span class="dep-amount-val">{{ formatAmount(dep.amount, dep.asset) }}</span>
                    <span class="dep-amount-meta">через {{ dep.paymentMethod }}</span>
                  </div>
                  <div v-if="dep.operatorEmail || dep.operatorName" class="dep-meta">
                    {{ dep.source === 'MANUAL_CREDIT' ? 'Начислил' : 'Подтвердил' }}:
                    <b>{{ dep.operatorName || dep.operatorEmail }}</b>
                    <span v-if="dep.operatorName && dep.operatorEmail" class="dep-meta-soft">· {{ dep.operatorEmail }}</span>
                  </div>
                  <div v-if="dep.bankReference" class="dep-meta">Reference: <span class="mono">{{ dep.bankReference }}</span></div>
                  <div v-if="dep.txHash" class="dep-meta">tx: <span class="mono">{{ dep.txHash }}</span></div>
                  <div v-if="dep.operatorComment" class="dep-meta">Комментарий: {{ dep.operatorComment }}</div>
                  <div v-if="dep.rejectedReason" class="dep-meta dep-meta-red">Причина отказа: {{ dep.rejectedReason }}</div>
                </div>
              </div>
            </div>
            <div v-else class="muted-mini">Депозитов пока не было</div>
          </div>
        </section>

        <!-- 9. ИСТОРИЯ ИЗМЕНЕНИЙ -->
        <section class="section">
          <header class="sec-head" @click="toggleSection('history')">
            <div class="sec-title"><span class="i">⏱</span> История изменений анкеты</div>
            <span class="sec-arrow" :class="{ open: openSections.history }">▾</span>
          </header>
          <div class="sec-body" :class="{ open: openSections.history }">
            <template v-if="historyItems.length">
              <div class="log-item" v-for="(event, idx) in historyItems" :key="idx">
                <div class="log-head">
                  <div class="log-type">
                    <span class="log-dot" :style="{ background: event.dotColor }"></span>
                    <span :style="{ color: event.textColor }">{{ event.title }}</span>
                  </div>
                  <div class="log-date">{{ event.when }}</div>
                </div>
                <div class="log-admin">{{ event.actor }}</div>
                <div class="log-note">{{ event.note }}</div>
              </div>
            </template>
            <div v-else class="muted-mini">Реклассификаций не было</div>
          </div>
        </section>
      </template>

      <div v-if="loading" class="dossier-loader">Загрузка досье…</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '../../config/config'

const ACCOUNT_STATUS_MAP = {
  ACTIVE:     { label: 'active',     css: 'b-act' },
  PENDING:    { label: 'pending',    css: 'b-pnd' },
  REJECTED:   { label: 'rejected',   css: 'b-rej' },
  BLOCKED:    { label: 'blocked',    css: 'b-blk' },
  TERMINATED: { label: 'terminated', css: 'b-term' },
  CLOSED:     { label: 'closed',     css: 'b-cls' },
}

const CLASSIFICATION_LABELS = {
  NONE:         'Не классифицирован',
  RETAIL_NQ:    'Retail (Not Assessed)',
  RETAIL_Q:     'Retail (Assessed)',
  PROFESSIONAL: 'Professional',
}

export default {
  name: 'UserDossier',
  props: {
    userId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      user: {},
      countries: [],
      docTypes: [],
      requests: [],
      snapshots: [],
      blockBusy: false,
      // Заморозка аккаунта — обязательный комментарий для аудита.
      freezeBusy: false,
      freezeReason: '',
      // Ручное переопределение риска (комплаенс). Приоритетнее
      // AMLBot-скоринга при отображении уровня риска.
      riskOverrideCurrent: null,

      openSections: {
        personal: true,
        document: true,
        address: true,
        finance: true,
        classification: true,
        aml: true,
        docs: true,
        generalDocs: true,
        balances: true,
        history: true,
      },

      // Балансы клиента + история его депозитов. Грузятся отдельным
      // вызовом /admin/funding/user/{id}/*, так что секция «Балансы и
      // депозиты» рендерится из локального стейта без блокировки
      // остальных секций при медленном ответе.
      balances: [],
      deposits: [],

      // For now only one anketa row reflects the current snapshot;
      // multi-anketa history slots in once /ankets backend lands.
      selectedAnketNum: 1,
    }
  },
  computed: {
    resolvedUserId() {
      return this.userId || this.$route.params.userId
    },
    fioFull() {
      const parts = [this.user.lastName, this.user.firstName, this.user.thirdName].filter(Boolean)
      return parts.join(' ')
    },
    initials() {
      const first = (this.user.firstName || '').trim().charAt(0)
      const last = (this.user.lastName || '').trim().charAt(0)
      return (last + first).toUpperCase() || '—'
    },
    doc() {
      return this.user.document || {}
    },
    docTypeLabel() {
      const code = this.doc.documentType
      const found = (this.docTypes || []).find((item) => String(item.id) === String(code) || item.code === code)
      return found ? (found.name || found.title || code) : code
    },
    isBlocked() {
      return (this.user.accountStatus || '').toUpperCase() === 'BLOCKED' || this.user.isActive === 0 || this.user.isActive === false
    },
    // Заморожен ли аккаунт (account_frozen) — отдельно от блокировки.
    isAccountFrozen() {
      const v = this.user && this.user.accountFrozen
      return v === 1 || v === '1' || v === true
    },
    accountStatusKey() {
      // Blocked всегда побеждает: у заблокированного пользователя
      // его onboarding-статус не важен для UX-индикатора.
      if (this.isBlocked) return 'BLOCKED'
      // Бэкенд хранит onboarding_status как PENDING/APPROVED/REJECTED
      // — это статусы «KYC-онбординга», не account status. В мок-
      // дизайне статусы аккаунта другие (active/pending/rejected/…),
      // поэтому мапим. Без этого APPROVED-юзер показывался как
      // PENDING (нет ключа APPROVED в ACCOUNT_STATUS_MAP).
      const onboarding = (this.user.onboardingStatus || '').toUpperCase()
      const onboardingMap = {
        APPROVED: 'ACTIVE',
        PENDING:  'PENDING',
        REJECTED: 'REJECTED',
      }
      if (onboardingMap[onboarding]) return onboardingMap[onboarding]
      const fallback = (this.user.accountStatus || 'PENDING').toUpperCase()
      return ACCOUNT_STATUS_MAP[fallback] ? fallback : 'PENDING'
    },
    accountStatusLabel() {
      return (ACCOUNT_STATUS_MAP[this.accountStatusKey] || {}).label || 'pending'
    },
    accountStatusClass() {
      return (ACCOUNT_STATUS_MAP[this.accountStatusKey] || {}).css || 'b-cls'
    },
    classificationLabel() {
      return CLASSIFICATION_LABELS[this.user.clientCategory] || 'Не классифицирован'
    },
    limitLabel() {
      const limit = this.user.limitInvestment
      if (limit == null || limit === '') return '—'
      if (Number(limit) === 0) return 'Без лимита'
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(limit) + ' $'
    },
    amlScore() {
      const raw = Number(this.user.amlScore || 0)
      return Number.isFinite(raw) ? raw : 0
    },
    riskTier() {
      // Ручное переопределение комплаенса приоритетнее AMLBot-скоринга.
      const ov = this.riskOverrideCurrent && this.riskOverrideCurrent.riskLevel
      if (ov) {
        const v = String(ov).toUpperCase()
        if (v === 'HIGH' || v === 'CRITICAL') return 'high'
        if (v === 'MEDIUM') return 'mid'
        if (v === 'LOW') return 'low'
      }
      // «Низкий» не показываем — минимальный отображаемый уровень
      // «Средний» проставляется автоматически.
      const score = this.amlScore
      if (score >= 70) return 'high'
      return 'mid'
    },
    // Признак ручного переопределения — для метки «вручную» в UI.
    riskIsOverridden() {
      return !!(this.riskOverrideCurrent && this.riskOverrideCurrent.riskLevel)
    },
    riskLabel() {
      return { high: 'Высокий', mid: 'Средний', low: 'Низкий' }[this.riskTier]
    },
    riskColorClass() {
      return { high: 'risk-high', mid: 'risk-mid', low: 'risk-low' }[this.riskTier]
    },
    amlScoreColorClass() {
      return { high: 'aml-score-h', mid: 'aml-score-m', low: 'aml-score-l' }[this.riskTier]
    },
    amlScoreFillColor() {
      return { high: '#EF4444', mid: '#F59E0B', low: '#10B981' }[this.riskTier]
    },
    pepInvolved() {
      return this.user.isPep === true || this.user.isPep === 1
    },
    amlFlags() {
      const flags = []
      if (this.pepInvolved) flags.push({ code: 'PEP', cssClass: 'af-pep' })
      const raw = Array.isArray(this.user.amlFlags) ? this.user.amlFlags : []
      raw.forEach((flagCode) => {
        if (flagCode === 'PEP' && this.pepInvolved) return
        const cssClass = flagCode === 'SANCTIONS' || flagCode === 'SNC' ? 'af-snc' : 'af-aml'
        flags.push({ code: flagCode, cssClass })
      })
      return flags
    },
    amlFlagsLabel() {
      return this.amlFlags.length ? this.amlFlags.map((flag) => flag.code).join(', ') : 'Нет'
    },
    amlAlertStatus() {
      return this.user.amlAlertStatus || ''
    },
    amlMonitoringNote() {
      return this.user.amlMonitoringNote || ''
    },
    hasMedia() {
      return this.user.hasPhoto || this.user.hasVideo
    },
    anketas() {
      // Single-row representation until the backend exposes anketa
      // history. The user-visible structure already supports more rows.
      return [
        {
          num: 1,
          status: 'active',
          created: this.user.registeredAt ? this.formatDate(this.user.registeredAt) : '—',
          closed: '—',
          risk: this.riskLabel,
          riskCss: this.riskColorClass,
          cls: this.classificationLabel,
          limit: this.limitLabel,
          accountStatusLabel: this.accountStatusLabel,
          accountStatusCss: this.accountStatusClass,
        },
      ]
    },
    selectedAnket() {
      return this.anketas.find((anket) => anket.num === this.selectedAnketNum) || this.anketas[0] || null
    },
    reclassifications() {
      return (this.requests || [])
        .filter((req) => req.requestedCategory && req.requestedCategory !== 'NONE')
        .map((req) => this.formatReclassification(req))
    },
    docList() {
      const docs = Array.isArray(this.user.documents) ? this.user.documents : []
      return docs.map((doc) => ({
        id: doc.id || doc.docType,
        title: doc.title || doc.docType || 'Документ',
        meta: this.docMeta(doc),
        statusLabel: this.docStatusLabel(doc.status),
        statusCss: this.docStatusCss(doc.status),
        docPath: doc.docPath || doc.filePath || doc.fileUrl || '',
      }))
    },
    // Pull documents off classification requests (where the backend
    // actually stores them) and bucket by reviewer responsibility:
    // Финансовые = SoF docs (MLRO), Квалификационные = certs + trade
    // history (Compliance officer). Empty groups are hidden in the
    // template so the section reads cleanly for clients who only have
    // one kind on file.
    clientGeneralDocs() {
      const requests = Array.isArray(this.requests) ? this.requests : []
      const allDocs = []
      requests.forEach((req) => {
        const docs = Array.isArray(req.documents) ? req.documents : []
        docs.forEach((doc) => allDocs.push(doc))
      })
      // Dedupe by (docType, fileUrl) keeping the most-recent decision.
      const byKey = new Map()
      allDocs.forEach((doc) => {
        const key = (doc.docType || '') + '|' + (doc.fileUrl || doc.fileName || doc.docId || '')
        const prev = byKey.get(key)
        if (!prev || new Date(doc.uploadedAt || 0) > new Date(prev.uploadedAt || 0)) byKey.set(key, doc)
      })
      const docs = Array.from(byKey.values())
      const isFin = (doc) => doc.docType === 'QUALIFIED' || doc.docType === 'PROFESSIONAL_SOF'
      const isQual = (doc) =>
        doc.docType === 'CERTIFICATE' || doc.docType === 'PROFESSIONAL_CERT' || doc.docType === 'PROFESSIONAL_HISTORY'
      const toItem = (doc) => ({
        id: doc.docId || doc.fileUrl || doc.docType,
        title: this.docTypeTitle(doc.docType),
        meta: this.docMeta(doc),
        statusLabel: this.docStatusLabel(doc.docStatus),
        statusCss: this.docStatusCss(doc.docStatus),
        docPath: doc.docPath || doc.filePath || doc.fileUrl || '',
      })
      // Вверху — только ОДОБРЕННЫЕ документы (actual decision = APPROVED).
      // Все неодобренные (отклонённые / на проверке) уходят вниз, в
      // «Историю документов».
      const relevant = docs.filter((doc) => isFin(doc) || isQual(doc))
      const approved = relevant.filter((doc) => doc.docStatus === 'APPROVED')
      const history = relevant
        .filter((doc) => doc.docStatus !== 'APPROVED')
        .map((doc) => ({
          ...toItem(doc),
          group: isFin(doc) ? 'Финансовые · MLRO' : 'Квалификационные · Комплаенс',
        }))
      return {
        financial: approved.filter(isFin).map(toItem),
        qualifying: approved.filter(isQual).map(toItem),
        history,
      }
    },
    hasClientGeneralDocs() {
      const general = this.clientGeneralDocs
      return general.financial.length || general.qualifying.length || general.history.length
    },
    historyItems() {
      // Показываем ТОЛЬКО историю реклассификаций (заявки на смену
      // категории клиента), не профильные снапшоты. Каждая заявка —
      // одна строка лога: from → to, статус, дата, ревьюер.
      const requests = Array.isArray(this.requests) ? this.requests.slice() : []
      return requests
        .filter((req) => req.requestedCategory && req.requestedCategory !== 'NONE')
        .sort((a, b) => new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt))
        .map((req) => this.formatReclassificationLog(req))
    },
  },
  watch: {
    resolvedUserId(newId, oldId) {
      if (newId && newId !== oldId) this.loadAll()
    },
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    // toast — единая обёртка над bvToast с дефолтами Payton-стиля.
    toast(message, title, variant) {
      if (!this.$bvToast) return
      this.$bvToast.toast(message, {
        title: title || 'Уведомление',
        variant: variant || 'info',
        toaster: 'b-toaster-top-center',
        solid: true,
        autoHideDelay: 4000,
      })
    },
    loadAll() {
      const userId = this.resolvedUserId
      if (!userId) return
      this.loading = true
      Promise.all([
        this.fetchUser(userId),
        this.fetchCountries(),
        this.fetchDocTypes(),
        this.fetchRequests(userId),
        this.fetchSnapshots(userId),
        this.fetchBalances(userId),
        this.fetchDeposits(userId),
        this.fetchRiskOverride(userId),
      ]).finally(() => { this.loading = false })
    },
    fetchUser(userId) {
      return axios
        .get(`${apiDomain}/exchange/userdetails/${userId}`, { headers: getHeader() })
        .then((res) => { this.user = res.data || {} })
        .catch((err) => { console.error('user load:', err) })
    },
    // Текущее ручное переопределение риска — приоритетнее AMLBot-скоринга.
    fetchRiskOverride(userId) {
      return axios
        .get(`${apiDomain}/exchange/userdetails/${userId}/risk`, { headers: getHeader() })
        .then((res) => { this.riskOverrideCurrent = res.data || null })
        .catch(() => { this.riskOverrideCurrent = null })
    },
    fetchCountries() {
      return axios
        .get(`${apiDomain}/exchange/country/list`, { headers: getHeader() })
        .then((res) => { this.countries = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.countries = [] })
    },
    fetchDocTypes() {
      return axios
        .get(`${apiDomain}/exchange/doc/list`, { headers: getHeader() })
        .then((res) => { this.docTypes = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.docTypes = [] })
    },
    fetchRequests(userId) {
      return axios
        .get(`${apiDomain}/exchange/userdetails/${userId}/classification-requests`, { headers: getHeader() })
        .then((res) => { this.requests = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.requests = [] })
    },
    fetchSnapshots(userId) {
      return axios
        .get(`${apiDomain}/exchange/userdetails/${userId}/snapshots`, { headers: getHeader() })
        .then((res) => { this.snapshots = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.snapshots = [] })
    },
    fetchBalances(userId) {
      return axios
        .get(`${apiDomain}/admin/funding/user/${userId}/balances`, { headers: getHeader() })
        .then((res) => { this.balances = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.balances = [] })
    },
    fetchDeposits(userId) {
      return axios
        .get(`${apiDomain}/admin/funding/user/${userId}/deposits`, { headers: getHeader() })
        .then((res) => { this.deposits = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.deposits = [] })
    },

    countryLabel(code) {
      if (!code) return ''
      const found = (this.countries || []).find((country) => country.code === code || country.alpha2 === code || country.id === code)
      if (!found) return code
      return found.name || found.title || code
    },
    fallback(value) {
      return value && value !== '—' && value !== '' ? value : 'не заполнено'
    },
    cellClass(value) {
      return value && value !== '—' && value !== '' ? 'v' : 'v e'
    },
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    anketWord(count) {
      if (count === 1) return 'анкета'
      if (count >= 2 && count <= 4) return 'анкеты'
      return 'анкет'
    },
    selectAnket(num) {
      this.selectedAnketNum = num
    },
    toggleSection(key) {
      this.openSections[key] = !this.openSections[key]
    },
    formatAmount(value, asset) {
      const fractionDigits = asset === 'USD' ? 2 : 8
      const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: fractionDigits }).format(Number(value) || 0)
      return formatted + ' ' + (asset || '')
    },
    depositStatusLabel(status) {
      switch ((status || '').toUpperCase()) {
        case 'PENDING':   return 'Ожидает'
        case 'CONFIRMED': return 'Подтверждён'
        case 'REJECTED':  return 'Отклонён'
        case 'EXPIRED':   return 'Истёк'
        default:          return status || '—'
      }
    },
    depositStatusCss(status) {
      switch ((status || '').toUpperCase()) {
        case 'PENDING':   return 'dep-status-pending'
        case 'CONFIRMED': return 'dep-status-ok'
        case 'REJECTED':  return 'dep-status-no'
        case 'EXPIRED':   return 'dep-status-muted'
        default:          return ''
      }
    },
    docMeta(doc) {
      const parts = []
      if (doc.mimeType) parts.push(doc.mimeType)
      if (doc.uploadedAt) parts.push('загружен ' + this.formatDate(doc.uploadedAt))
      return parts.join(' · ')
    },
    docStatusLabel(status) {
      switch ((status || '').toUpperCase()) {
        case 'APPROVED': return '✓ Загружен'
        case 'REJECTED': return '✕ Отклонён'
        case 'PENDING':  return 'На проверке'
        default:         return 'Нет данных'
      }
    },
    docStatusCss(status) {
      switch ((status || '').toUpperCase()) {
        case 'APPROVED': return 'ds-ok'
        case 'REJECTED': return 'ds-no'
        case 'PENDING':  return 'ds-pnd'
        default:         return 'ds-no'
      }
    },
    docTypeTitle(docType) {
      const map = {
        QUALIFIED:           'Источник происхождения средств (SoF)',
        PROFESSIONAL_SOF:    'Источник средств (Professional)',
        CERTIFICATE:         'Сертификат CFA / FRM / CAIA / CIIA',
        PROFESSIONAL_CERT:   'Сертификат или диплом',
        PROFESSIONAL_HISTORY:'История торговой активности',
      }
      return map[docType] || docType || 'Документ'
    },
    // Реклассификация как строка лога для секции «История изменений
    // анкеты» — {title, when, actor, note, dotColor, textColor}.
    formatReclassificationLog(req) {
      const fromLabel = CLASSIFICATION_LABELS[req.fromCategory] || req.fromCategory || '—'
      const toLabel = CLASSIFICATION_LABELS[req.requestedCategory] || req.requestedCategory || '—'
      const status = (req.status || '').toUpperCase()
      const map = {
        APPROVED: { note: 'Одобрена',        color: '#065F46', dot: '#10B981' },
        REJECTED: { note: 'Отклонена',       color: '#DC2626', dot: '#EF4444' },
        PENDING:  { note: 'На рассмотрении',  color: '#D97706', dot: '#F59E0B' },
        DRAFT:    { note: 'Черновик',         color: '#6B7280', dot: '#9CA3AF' },
      }
      const visual = map[status] || { note: status || '—', color: '#6B7280', dot: '#9CA3AF' }
      return {
        title: 'Реклассификация: ' + fromLabel + ' → ' + toLabel,
        when: this.formatDateTime(req.submittedAt || req.createdAt) || '—',
        actor: req.reviewerName || req.reviewer || '',
        note: visual.note,
        dotColor: visual.dot,
        textColor: visual.color,
      }
    },
    formatReclassification(req) {
      const fromLabel = CLASSIFICATION_LABELS[req.fromCategory] || req.fromCategory || '—'
      const toLabel = CLASSIFICATION_LABELS[req.requestedCategory] || req.requestedCategory || '—'
      const submittedAt = this.formatDateTime(req.submittedAt || req.createdAt) || '—'
      const status = (req.status || '').toUpperCase()
      const map = {
        APPROVED: { label: '✓ Одобрена',       color: '#065F46' },
        REJECTED: { label: '✕ Отклонена',      color: '#DC2626' },
        PENDING:  { label: '⏳ На рассмотрении', color: '#D97706' },
        DRAFT:    { label: '✎ Черновик',       color: '#6B7280' },
      }
      const visual = map[status] || { label: status || '—', color: '#6B7280' }

      // Per-role votes aren't exposed on /classification-requests yet,
      // so derive the MLRO / Compliance lanes from per-doc decisions
      // — MLRO reviews SoF (QUALIFIED / PROFESSIONAL_SOF), Compliance
      // handles the certificate + trade history slots. Once a proper
      // per-role audit endpoint lands we can swap this in.
      const documents = Array.isArray(req.documents) ? req.documents : []
      const mlroDocs = documents.filter((doc) => doc.docType === 'QUALIFIED' || doc.docType === 'PROFESSIONAL_SOF')
      const complianceDocs = documents.filter((doc) =>
        doc.docType === 'CERTIFICATE' || doc.docType === 'PROFESSIONAL_CERT' || doc.docType === 'PROFESSIONAL_HISTORY')

      const mlro = this.summariseDocVote(mlroDocs)
      const compliance = this.summariseDocVote(complianceDocs)
      const complianceBorder = compliance ? { borderColor: compliance.action === 'approve' ? '#A7F3D0' : '#FECACA' } : {}
      const mlroBorder = mlro ? { borderColor: mlro.action === 'approve' ? '#A7F3D0' : '#FECACA' } : {}

      // Нужно ли решение Комплаенс-офицера. Путь через тест (attemptId у
      // RETAIL_Q без сертификата) не требует подтверждения комплаенса.
      let needsCompliance = complianceDocs.length > 0
      if (req.requestedCategory === 'RETAIL_Q' && !req.attemptId) needsCompliance = true

      return {
        from: fromLabel,
        to: toLabel,
        submittedAt,
        compliance,
        mlro,
        needsCompliance,
        resultLabel: visual.label,
        resultColor: visual.color,
        complianceBorder,
        mlroBorder,
      }
    },
    // Aggregate per-doc statuses into a single role-level verdict.
    // REJECTED dominates APPROVED so the worst slot drives the outcome
    // — same precedence the backend's RecomputeStatus uses.
    summariseDocVote(docs) {
      if (!docs.length) return null
      const order = (doc) => (doc.docStatus === 'REJECTED' ? 3 : doc.docStatus === 'APPROVED' ? 2 : 1)
      const sorted = docs.slice().sort((a, b) => order(b) - order(a))
      const worst = sorted[0]
      const status = (worst.docStatus || '').toUpperCase()
      if (status !== 'APPROVED' && status !== 'REJECTED') return null
      const reviewerName = worst.reviewerName || worst.reviewer || ''
      return {
        action: status === 'APPROVED' ? 'approve' : 'reject',
        label: status === 'APPROVED' ? '✓ Одобрил' : '✕ Отклонил',
        color: status === 'APPROVED' ? '#065F46' : '#DC2626',
        comment: worst.reviewComment || reviewerName || '',
        when: this.formatDateTime(worst.reviewedAt || worst.uploadedAt) || '',
      }
    },
    formatHistoryEvent(entry) {
      const action = (entry.action || entry.type || '').toUpperCase()
      const map = {
        CREATED:           { title: 'Регистрация', dotColor: '#6366F1', textColor: '#4338CA' },
        KYC_VERIFIED:      { title: 'KYC Verified', dotColor: '#10B981', textColor: '#065F46' },
        STATUS_CHANGED:    { title: 'Изменение статуса', dotColor: '#F59E0B', textColor: '#92400E' },
        CLASSIFICATION:    { title: 'Реклассификация', dotColor: '#10B981', textColor: '#065F46' },
        BLOCKED:           { title: 'Заблокирован', dotColor: '#F43F5E', textColor: '#9F1239' },
        UNBLOCKED:         { title: 'Разблокирован', dotColor: '#10B981', textColor: '#065F46' },
      }
      const visual = map[action] || { title: action || 'Событие', dotColor: '#9CA3AF', textColor: '#6B7280' }
      return {
        title: visual.title,
        dotColor: visual.dotColor,
        textColor: visual.textColor,
        when: this.formatDateTime(entry.changedAt || entry.createdAt) || '—',
        actor: entry.actor || entry.changedBy || 'Система',
        note: entry.note || entry.details || '—',
      }
    },

    // Same endpoint the side-panel UserDetail uses — keep both views
    // hitting one download path so a file rotates in one place.
    docFileName(path) {
      if (!path) return ''
      const parts = String(path).split(/[\\/]/)
      return parts[parts.length - 1] || ''
    },
    downloadDocItem(item) {
      if (!item || !item.docPath) {
        this.toast('Этот файл недоступен для скачивания.', 'Нет файла', 'warning')
        return
      }
      const fileName = this.docFileName(item.docPath)
      if (!fileName) return
      const url = `${apiDomain}/ptest/downloadclientfile/${encodeURIComponent(fileName)}`
      axios.get(url, { headers: getHeader(), responseType: 'blob' })
        .then((response) => {
          const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = fileName
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch((err) => {
          console.error('download failed:', err)
          this.toast('Не удалось скачать файл.', 'Ошибка', 'danger')
        })
    },

    onPrint() { window.print() },
    onExportPdf() {
      // Placeholder until a real PDF endpoint lands. window.print → save-as-PDF is the
      // cheapest interim, and the layout already includes @media print rules.
      window.print()
    },
    toggleBlock() {
      const userId = this.resolvedUserId
      if (!userId) return
      const next = !this.isBlocked
      const confirmText = next ? 'Заблокировать клиента?' : 'Разблокировать клиента?'
      if (!window.confirm(confirmText)) return
      this.blockBusy = true
      axios.post(`${apiDomain}/user/changestatus`, {
        userId: Number(userId),
        isActive: next ? 0 : 1,
      }, { headers: getHeader() })
        .then(() => this.fetchUser(userId))
        .catch((err) => {
          console.error('toggleBlock failed:', err)
          this.toast('Не удалось изменить статус.', 'Ошибка', 'danger')
        })
        .finally(() => { this.blockBusy = false })
    },
    // ---- Заморозка / разморозка аккаунта ----
    openFreezeModal() {
      this.freezeReason = ''
      this.$bvModal && this.$bvModal.show('freeze-dossier-modal')
    },
    submitFreeze() {
      const userId = this.resolvedUserId
      if (!userId) return
      const reason = (this.freezeReason || '').trim()
      if (!reason) return // комментарий для аудита обязателен
      const frozen = !this.isAccountFrozen
      this.freezeBusy = true
      axios.post(`${apiDomain}/user/freeze-status`, {
        userId: Number(userId), frozen, reason,
      }, { headers: getHeader() })
        .then(() => {
          this.toast(frozen ? 'Аккаунт заморожен. Клиент уведомлён.' : 'Заморозка снята. Клиент уведомлён.', 'Готово', 'success')
          this.freezeReason = ''
          this.$bvModal && this.$bvModal.hide('freeze-dossier-modal')
          this.fetchUser(userId)
        })
        .catch((err) => {
          const sc = err && err.response && err.response.status
          console.error('freeze failed:', err)
          this.toast(sc === 401 || sc === 403 ? 'Недостаточно прав.' : 'Не удалось изменить состояние заморозки.', 'Ошибка', 'danger')
        })
        .finally(() => { this.freezeBusy = false })
    },
  },
}
</script>

<style scoped>
.dossier-page {
  font-family: 'Inter', system-ui, sans-serif;
  background: #F0F2F5;
  color: #111;
  font-size: 13px;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
}

.ud-nav {
  height: 54px;
  background: #fff;
  border-bottom: 1px solid #EAECF0;
  display: flex;
  align-items: center;
  padding: 0 24px;
  gap: 10px;
  position: sticky;
  top: 0;
  z-index: 30;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.brand { font-size: 15px; font-weight: 800; color: #111; }
.brand em { color: #16A37F; font-style: normal; }
.bc-sep { color: #D1D5DB; font-size: 16px; }
.bc-link { font-size: 12px; color: #6B7280; cursor: pointer; transition: color .15s; text-decoration: none; }
.bc-link:hover { color: #16A37F; }
.bc-cur { font-size: 12px; color: #374151; font-weight: 600; }

.nav-r { margin-left: auto; display: flex; align-items: center; gap: 8px; }
.nav-btn {
  display: flex; align-items: center; gap: 5px;
  height: 32px; padding: 0 12px;
  border: 1px solid #EAECF0; border-radius: 8px;
  background: #fff;
  font-size: 12px; font-weight: 600;
  cursor: pointer; font-family: inherit; color: #374151;
  transition: all .15s;
}
.nav-btn:hover { background: #F9FAFB; }
.nav-btn.danger { background: #FEF2F2; border-color: #FECACA; color: #DC2626; }
.nav-btn.danger:hover { background: #FEE2E2; }
.nav-btn.warn { background: #FFFBEB; border-color: #FDE68A; color: #B45309; }
.nav-btn.warn:hover { background: #FEF3C7; }
.nav-btn .i { font-size: 13px; }

.wrap { max-width: 1100px; margin: 0 auto; padding: 24px 24px 60px; }

.ph { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 20px; }
.ph-left { display: flex; align-items: center; }
.ph-ava {
  width: 52px; height: 52px;
  border-radius: 50%;
  background: #EDFAF5;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 800; color: #0D7A5F;
  flex-shrink: 0; margin-right: 14px;
}
.ph-name { font-size: 22px; font-weight: 800; color: #0A0A0A; letter-spacing: -.03em; }
.ph-meta { display: flex; align-items: center; gap: 10px; margin-top: 5px; flex-wrap: wrap; font-size: 12px; color: #9CA3AF; }
.ph-meta b { color: #374151; font-weight: 600; }

.badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 9px; border-radius: 6px;
  font-size: 11px; font-weight: 700;
  white-space: nowrap;
}
.bd { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
.b-act  { background: #ECFDF5; color: #065F46; } .b-act  .bd { background: #10B981; }
.b-pnd  { background: #FFFBEB; color: #92400E; } .b-pnd  .bd { background: #F59E0B; }
.b-rej  { background: #FEF2F2; color: #991B1B; } .b-rej  .bd { background: #EF4444; }
.b-blk  { background: #FFF1F2; color: #9F1239; } .b-blk  .bd { background: #F43F5E; }
.b-term { background: #F5F3FF; color: #5B21B6; } .b-term .bd { background: #8B5CF6; }
.b-cls  { background: #F3F4F6; color: #6B7280; } .b-cls  .bd { background: #9CA3AF; }

.risk-high { color: #DC2626; font-weight: 700; }
.risk-mid  { color: #D97706; font-weight: 700; }
.risk-low  { color: #059669; font-weight: 700; }
.risk-manual-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #EEF2FF;
  color: #4338CA;
  font-size: 10px;
  font-weight: 700;
  vertical-align: middle;
}

.aml-flag {
  display: inline-block; padding: 1px 6px;
  border-radius: 4px;
  font-size: 9px; font-weight: 800;
}
.af-pep { background: #FEF3C7; color: #92400E; }
.af-snc { background: #FEE2E2; color: #991B1B; }
.af-aml { background: #FFF1F2; color: #9F1239; }

.status-bar { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 20px; }
.sb-item { background: #fff; border: 1px solid #EAECF0; border-radius: 10px; padding: 12px 14px; }
.sb-lbl { font-size: 9.5px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 4px; }
.sb-val { font-size: 13px; font-weight: 700; color: #0A0A0A; }

.anket-panel { background: #fff; border: 1px solid #EAECF0; border-radius: 12px; margin-bottom: 20px; overflow: hidden; }
.anket-panel-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; background: #FAFAFA; border-bottom: 1px solid #F0F2F5; }
.anket-head-left { display: flex; align-items: center; gap: 7px; }
.anket-panel-title { font-size: 13px; font-weight: 700; color: #0A0A0A; }
.anket-cnt { font-size: 11px; font-weight: 700; background: #EDFAF5; color: #0D7A5F; padding: 2px 7px; border-radius: 20px; margin-left: 4px; }
.dot-ic { font-size: 15px; }
.muted-mini { font-size: 11px; color: #9CA3AF; }

table.at { width: 100%; border-collapse: collapse; }
table.at thead th { padding: 8px 14px; text-align: left; font-size: 9.5px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: .06em; border-bottom: 1px solid #F0F2F5; white-space: nowrap; background: #FAFAFA; }
table.at tbody tr { cursor: pointer; transition: background .12s; }
table.at tbody tr:hover { background: #F0FBF8; }
table.at tbody tr.sel td { background: #EDFAF5 !important; }
table.at tbody td { padding: 11px 14px; border-bottom: 1px solid #F5F6F8; vertical-align: middle; font-size: 12.5px; color: #374151; }
table.at tbody tr:last-child td { border-bottom: none; }
.row-arrow { width: 28px; text-align: center; color: #D1D5DB; font-size: 14px; }
table.at tbody tr:hover .row-arrow,
table.at tbody tr.sel .row-arrow { color: #16A37F; }
.a-num { width: 24px; height: 24px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; }
.a-num.act { background: #EDFAF5; color: #0D7A5F; }
.a-num.arch { background: #F3F4F6; color: #9CA3AF; }
.a-badge { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.a-badge.active { background: #ECFDF5; color: #065F46; }
.a-badge.active::before { content: '●'; font-size: 7px; color: #10B981; }
.a-badge.arch { background: #F3F4F6; color: #9CA3AF; }
.a-badge.arch::before { content: '○'; font-size: 8px; }

.dos-label { display: flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 10px; margin-bottom: 16px; font-size: 12px; font-weight: 600; }
.dos-label.active { background: #ECFDF5; border: 1px solid #6EE7B7; color: #065F46; }
.dos-label.archive { background: #F9FAFB; border: 1px solid #E5E7EB; color: #6B7280; }
.dos-label .i { font-size: 16px; }

.section { background: #fff; border: 1px solid #EAECF0; border-radius: 12px; margin-bottom: 12px; overflow: hidden; }
.sec-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-bottom: 1px solid #F0F2F5; cursor: pointer; user-select: none; transition: background .15s; }
.sec-head:hover { background: #FAFAFA; }
.sec-title { font-size: 13px; font-weight: 700; color: #0A0A0A; display: flex; align-items: center; gap: 8px; }
.sec-title .i { font-size: 16px; color: #16A37F; }
.sec-arrow { font-size: 14px; color: #9CA3AF; transition: transform .2s; }
.sec-arrow.open { transform: rotate(180deg); }
.sec-body { padding: 16px 18px; display: none; }
.sec-body.open { display: block; }

.fg { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 12px; }
.f { background: #F9FAFB; border-radius: 8px; padding: 10px 12px; }
.f.full { grid-column: span 3; }
.f label { font-size: 9px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: .07em; display: block; margin-bottom: 4px; }
.f .v { font-size: 12.5px; font-weight: 600; color: #0A0A0A; }
.f .v.e { color: #D1D5DB; font-weight: 400; font-style: italic; font-size: 11.5px; }
.subsec { font-size: 10px; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: .07em; margin: 14px 0 8px; padding-bottom: 6px; border-bottom: 1px solid #F0F2F5; }

.doc-groups { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.dg-title { font-size: 10px; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: .07em; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #F0F2F5; }
.dg-history { grid-column: 1 / -1; margin-top: 4px; padding-top: 10px; border-top: 1px dashed #E5E7EB; }
.doc-item { display: flex; align-items: center; gap: 12px; padding: 10px; border: 1px solid #EAECF0; border-radius: 9px; margin-bottom: 7px; cursor: pointer; transition: all .12s; }
.doc-item:hover { border-color: #16A37F; background: #F0FBF8; }
.doc-item-disabled { cursor: default; opacity: .65; }
.doc-item-disabled:hover { border-color: #EAECF0; background: #fff; }
.doc-info { flex: 1; min-width: 0; }
.doc-dl {
  border: 1px solid #EAECF0; background: #fff;
  width: 30px; height: 30px;
  border-radius: 7px; cursor: pointer;
  color: #6B7280; font-size: 14px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  transition: all .12s;
  margin-left: 8px;
}
.doc-dl:hover { background: #ECFDF5; border-color: #16A37F; color: #0D7A5F; }
.doc-dl:disabled { cursor: not-allowed; opacity: .4; }
.doc-dl:disabled:hover { background: #fff; border-color: #EAECF0; color: #6B7280; }
.doc-thumb { width: 32px; height: 32px; border-radius: 7px; background: #F0F2F5; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 16px; }
.doc-name { font-size: 12px; font-weight: 600; color: #0A0A0A; }
.doc-meta { font-size: 10px; color: #9CA3AF; margin-top: 2px; }
.ds { margin-left: auto; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 5px; white-space: nowrap; }
.ds-ok { background: #ECFDF5; color: #065F46; }
.ds-pnd { background: #FFFBEB; color: #92400E; }
.ds-no { background: #F3F4F6; color: #9CA3AF; }

.log-item { padding: 10px 12px; border: 1px solid #EAECF0; border-radius: 8px; margin-bottom: 7px; }
.log-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.log-type { font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 5px; }
.log-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.log-date { font-size: 10px; color: #9CA3AF; }
.log-admin { font-size: 10px; color: #6B7280; margin-bottom: 4px; }
.log-note { font-size: 11px; color: #374151; background: #F9FAFB; border-radius: 5px; padding: 5px 8px; border-left: 2px solid #E5E7EB; }

.aml-score-wrap { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.aml-score-num { font-size: 32px; font-weight: 800; letter-spacing: -.03em; }
.aml-score-h { color: #DC2626; }
.aml-score-m { color: #D97706; }
.aml-score-l { color: #059669; }
.aml-score-caption { font-size: 11px; font-weight: 600; color: #374151; margin-bottom: 4px; }
.aml-score-bar { width: 200px; height: 8px; background: #F0F2F5; border-radius: 4px; overflow: hidden; }
.aml-score-fill { height: 100%; border-radius: 4px; transition: width .3s; }

.recls-item { border: 1px solid #EAECF0; border-radius: 9px; padding: 12px 14px; margin-bottom: 8px; }
.recls-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.recls-title { font-size: 12px; font-weight: 700; color: #0A0A0A; }
.recls-result { font-size: 11px; font-weight: 700; }
.recls-date { font-size: 10px; color: #9CA3AF; margin-bottom: 8px; }
.recls-votes { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.rv-box { border-radius: 7px; padding: 8px 10px; border: 1px solid #EAECF0; }
.rv-role { font-size: 9px; font-weight: 700; color: #9CA3AF; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 3px; }
.rv-result { font-size: 11px; font-weight: 700; margin-bottom: 2px; }
.rv-comment { font-size: 10px; color: #6B7280; font-style: italic; }
.rv-when { font-size: 9px; color: #C0C4CC; margin-top: 3px; }
.rv-pending { font-size: 11px; color: #9CA3AF; font-style: italic; }

.media-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 12px; }
.media-card { border: 1px solid #EAECF0; border-radius: 12px; overflow: hidden; background: #fff; }
.media-card-head { padding: 10px 14px; border-bottom: 1px solid #F0F2F5; display: flex; align-items: center; justify-content: space-between; }
.media-card-title { font-size: 11px; font-weight: 700; color: #0A0A0A; }
.media-card-status { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 5px; }
.mcs-ok { background: #ECFDF5; color: #065F46; }
.mcs-no { background: #F3F4F6; color: #9CA3AF; }
.media-card-body { padding: 0; background: #F0F2F5; min-height: 220px; display: flex; align-items: center; justify-content: center; }
.photo-placeholder, .video-placeholder { text-align: center; padding: 24px; }
.photo-placeholder-icon, .video-placeholder-icon {
  width: 80px; height: 80px;
  border-radius: 50%;
  background: #fff;
  border: 2px dashed #D1D5DB;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 12px;
  font-size: 28px;
}
.video-placeholder-icon { width: 80px; height: 56px; border-radius: 10px; }
.photo-placeholder-text { font-size: 12px; color: #9CA3AF; font-weight: 500; }
.photo-placeholder-sub { font-size: 10px; color: #C0C4CC; margin-top: 4px; }
.photo-avatar-large { width: 100%; height: 220px; background: linear-gradient(135deg, #EDFAF5, #D1FAE5); display: flex; align-items: center; justify-content: center; font-size: 64px; font-weight: 800; color: #0D7A5F; letter-spacing: -0.02em; }

.dossier-loader { text-align: center; color: #9CA3AF; font-size: 12px; padding: 24px; }

.bal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 10px;
  margin-bottom: 8px;
}
.bal-card {
  border: 1px solid #EAECF0;
  border-radius: 10px;
  padding: 11px 13px;
  background: #FAFCFB;
}
.bal-asset {
  font-size: 13px;
  font-weight: 800;
  color: #0A0A0A;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.bal-net {
  font-size: 9.5px;
  font-weight: 700;
  color: #6B7280;
  background: #F3F4F6;
  padding: 1px 6px;
  border-radius: 4px;
  letter-spacing: 0.02em;
}
.bal-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  padding: 2px 0;
}
.bal-row-lbl { color: #6B7280; }
.bal-row-val { font-weight: 700; color: #0A0A0A; }
.bal-locked { color: #92400E; }

.dep-item {
  border: 1px solid #EAECF0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 7px;
}
.dep-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.dep-title { display: flex; align-items: center; gap: 8px; }
.dep-id { font-size: 11px; font-weight: 700; color: #6B7280; }
.dep-status { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 5px; }
.dep-status-ok { background: #ECFDF5; color: #065F46; }
.dep-status-pending { background: #FFFBEB; color: #92400E; }
.dep-status-no { background: #FEF2F2; color: #991B1B; }
.dep-status-muted { background: #F3F4F6; color: #6B7280; }
.dep-source { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 5px; background: #EFF6FF; color: #1D4ED8; }
.dep-date { font-size: 10.5px; color: #9CA3AF; }
.dep-body { display: flex; flex-direction: column; gap: 3px; }
.dep-amount { display: flex; align-items: baseline; gap: 8px; margin-bottom: 2px; }
.dep-amount-val { font-size: 14px; font-weight: 800; color: #0A0A0A; }
.dep-amount-meta { font-size: 10.5px; color: #6B7280; }
.dep-meta { font-size: 11px; color: #374151; }
.dep-meta .mono { font-family: ui-monospace, SFMono-Regular, monospace; color: #6B7280; }
.dep-meta-red { color: #991B1B; }
.dep-meta-soft { color: #9CA3AF; margin-left: 4px; font-size: 10.5px; }

@media print {
  .ud-nav, .nav-r { display: none !important; }
  .sec-body { display: block !important; }
  .section { break-inside: avoid; }
}
</style>
